import React, { useState, useEffect } from 'react';
import { config } from '../config';
import './MySubscriptions.css';

interface Subscription {
  id: string;
  bill_id: string;
  bill_title?: string;
  created_at: string;
  active: boolean;
}

interface MySubscriptionsProps {
  jwtToken: string | null;
  subscriptions: string[];
  onClose: () => void;
  onSubscriptionChange: (billId: string, isSubscribed: boolean) => void;
}

const MySubscriptions: React.FC<MySubscriptionsProps> = ({ 
  jwtToken, 
  subscriptions, 
  onClose, 
  onSubscriptionChange 
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleUnsubscribe = async (billId: string) => {
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await fetch(`${config.API_BASE_URL}/bills/${billId}/unsubscribe`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${jwtToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        onSubscriptionChange(billId, false);
      } else {
        setError('Failed to unsubscribe from bill');
      }
    } catch (error) {
      console.error('Error unsubscribing:', error);
      setError('Failed to unsubscribe from bill');
    } finally {
      setIsLoading(false);
    }
  };

  if (!jwtToken) {
    return null;
  }

  return (
    <div className="subscriptions-modal">
      <div className="subscriptions-content">
        <div className="subscriptions-header">
          <h2>📋 My Bill Subscriptions</h2>
          <button onClick={onClose} className="close-btn">×</button>
        </div>
        
        {error && (
          <div className="error-message" style={{ color: 'red', margin: '10px 0' }}>
            {error}
          </div>
        )}
        
        {isLoading ? (
          <div className="loading">Processing...</div>
        ) : subscriptions.length === 0 ? (
          <div className="no-subscriptions">
            <p>You're not following any bills yet.</p>
            <p>Click the "🔔 Follow Bill" button on any bill to get email notifications when it moves through Congress!</p>
          </div>
        ) : (
          <div className="subscriptions-list">
            {subscriptions.map((billId) => (
              <div key={billId} className="subscription-item">
                <div className="subscription-info">
                  <h3>{billId.toUpperCase()}</h3>
                  <p className="subscription-date">
                    Currently following this bill
                  </p>
                </div>
                <button
                  onClick={() => handleUnsubscribe(billId)}
                  className="unsubscribe-btn"
                  disabled={isLoading}
                >
                  {isLoading ? 'Unfollowing...' : 'Unfollow'}
                </button>
              </div>
            ))}
          </div>
        )}
        
        <div className="subscriptions-footer">
          <p>You'll receive email notifications when bills you follow have status changes.</p>
        </div>
      </div>
    </div>
  );
};

export default MySubscriptions;
